import { ApiClient } from '@api/api-configuration';
import { BillingEntityTypeEnum, BillingTransactionTypeEnum } from '@api/READ_ONLY/billing_api/Api';
import { BILLING_QUERY_KEYS } from '@app/common/queryKeys';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SimpleBar from 'simplebar-react';
import ConductPaymentForm from '../components/ConductPaymentForm/ConductPaymentForm';
import PaymentsSidebar from './PaymentsSidebar';
import type { PaymentFormValues, PaymentPayload } from './types';

const PAYMENT_QUERY_PARAM_KEYS = {
  ID: 'id',
  VISIT_ID: 'visitId',
  TYPE: 'type',
} as const;

const ENTITY_TYPE_PARAM_VALUES = {
  CLIENT: 'client',
  DOCTOR: 'doctor',
  CAREGIVER: 'caregiver',
} as const;

type EntityTypeParam = (typeof ENTITY_TYPE_PARAM_VALUES)[keyof typeof ENTITY_TYPE_PARAM_VALUES];

const parseNumberSearchParam = (value: string | null): number | undefined => {
  if (!value) {
    return undefined;
  }

  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const normalizeEntityTypeParam = (value: string | null): EntityTypeParam | undefined => {
  if (!value) {
    return undefined;
  }

  const normalized = value.toLowerCase();

  if (normalized === ENTITY_TYPE_PARAM_VALUES.CLIENT) {
    return ENTITY_TYPE_PARAM_VALUES.CLIENT;
  }

  if (normalized === ENTITY_TYPE_PARAM_VALUES.DOCTOR) {
    return ENTITY_TYPE_PARAM_VALUES.DOCTOR;
  }

  if (normalized === ENTITY_TYPE_PARAM_VALUES.CAREGIVER) {
    return ENTITY_TYPE_PARAM_VALUES.CAREGIVER;
  }

  return undefined;
};

const resolveStaffTypeFromEntityType = (entityType: EntityTypeParam | undefined): PaymentFormValues['staffType'] => {
  if (entityType === ENTITY_TYPE_PARAM_VALUES.DOCTOR) {
    return ENTITY_TYPE_PARAM_VALUES.DOCTOR;
  }

  if (entityType === ENTITY_TYPE_PARAM_VALUES.CAREGIVER) {
    return ENTITY_TYPE_PARAM_VALUES.CAREGIVER;
  }

  return undefined;
};

const createPayment = async (payload: PaymentPayload): Promise<void> => {
  if (payload.type === BillingTransactionTypeEnum.ClientPayment) {
    await ApiClient.billingApi.billing.createTransaction({
      type: BillingTransactionTypeEnum.ClientPayment,
      clientId: payload.clientId,
      ...(payload.visitId !== undefined ? { visitId: payload.visitId } : {}),
      date: payload.date,
      amount: payload.amount,
      notes: payload.notes ?? '',
    });
    return;
  }

  await ApiClient.billingApi.billing.createTransaction({
    type: payload.type,
    staffId: payload.staffId,
    date: payload.date,
    amount: payload.amount,
    notes: payload.notes ?? '',
  });
};

const Payments = () => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const parsedEntityId = parseNumberSearchParam(searchParams.get(PAYMENT_QUERY_PARAM_KEYS.ID));
  const resolvedVisitId = parseNumberSearchParam(searchParams.get(PAYMENT_QUERY_PARAM_KEYS.VISIT_ID));
  const normalizedEntityType = normalizeEntityTypeParam(searchParams.get(PAYMENT_QUERY_PARAM_KEYS.TYPE));

  const isStaffPaymentType =
    normalizedEntityType === ENTITY_TYPE_PARAM_VALUES.DOCTOR ||
    normalizedEntityType === ENTITY_TYPE_PARAM_VALUES.CAREGIVER;

  const resolvedPaymentType: PaymentFormValues['paymentType'] = isStaffPaymentType
    ? normalizedEntityType === ENTITY_TYPE_PARAM_VALUES.DOCTOR
      ? BillingTransactionTypeEnum.DoctorPayout
      : BillingTransactionTypeEnum.CaregiverPayout
    : BillingTransactionTypeEnum.ClientPayment;

  const resolvedStaffType = resolveStaffTypeFromEntityType(normalizedEntityType);

  const resolvedClientId =
    resolvedPaymentType === BillingTransactionTypeEnum.ClientPayment ? parsedEntityId : undefined;

  const resolvedStaffId = isStaffPaymentType ? parsedEntityId : undefined;

  const initialValues: PaymentFormValues = useMemo(
    () => ({
      paymentType: resolvedPaymentType,
      clientId: resolvedClientId,
      visitId: resolvedPaymentType === BillingTransactionTypeEnum.ClientPayment ? resolvedVisitId : undefined,
      staffType: resolvedStaffType,
      staffId: resolvedStaffId,
      date: dayjs(),
      amount: undefined,
      notes: '',
    }),
    [resolvedClientId, resolvedPaymentType, resolvedStaffId, resolvedStaffType, resolvedVisitId]
  );

  const initialSidebarEntityType = useMemo<BillingEntityTypeEnum | undefined>(() => {
    if (normalizedEntityType === ENTITY_TYPE_PARAM_VALUES.CLIENT || !normalizedEntityType) {
      return BillingEntityTypeEnum.Client;
    }

    if (normalizedEntityType === ENTITY_TYPE_PARAM_VALUES.DOCTOR) {
      return BillingEntityTypeEnum.Doctor;
    }

    if (normalizedEntityType === ENTITY_TYPE_PARAM_VALUES.CAREGIVER) {
      return BillingEntityTypeEnum.Caregiver;
    }

    return undefined;
  }, [normalizedEntityType]);

  const [sidebarEntityType, setSidebarEntityType] = useState<BillingEntityTypeEnum | undefined>(
    initialSidebarEntityType
  );
  const [formResetSignal, setFormResetSignal] = useState(0);

  useEffect(() => {
    setSidebarEntityType(initialSidebarEntityType);
  }, [initialSidebarEntityType]);

  const handleSidebarEntitySelect = () => {
    setFormResetSignal((currentValue) => currentValue + 1);
  };

  const createPaymentMutation = useMutation({
    mutationFn: createPayment,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [BILLING_QUERY_KEYS.RECENT_PAYMENTS_SIDEBAR] }),
        queryClient.invalidateQueries({ queryKey: [BILLING_QUERY_KEYS.PAYMENT_FORM_ENTITY_BALANCE] }),
        queryClient.invalidateQueries({ queryKey: [BILLING_QUERY_KEYS.BALANCES_SUMMARY] }),
        queryClient.invalidateQueries({ queryKey: [BILLING_QUERY_KEYS.BALANCES_LIST] }),
      ]);
    },
  });

  const handleCreatePayment = async (payload: PaymentPayload): Promise<void> => {
    await createPaymentMutation.mutateAsync(payload);
  };

  return (
    <SimpleBar className="max-h-[calc(100vh-100px)]">
      <div className="w-full bg-slate-50">
        <div className="grid grid-cols-1 gap-6 min-[1350px]:grid-cols-[minmax(0,820px)_minmax(450px,1fr)]">
          <div className="min-w-0">
            <ConductPaymentForm
              initialValues={initialValues}
              resetSignal={formResetSignal}
              onSubmit={handleCreatePayment}
              onSidebarEntityTypeChange={setSidebarEntityType}
              isSubmitting={createPaymentMutation.isPending}
            />
          </div>

          <div className="min-w-0">
            <PaymentsSidebar
              key={sidebarEntityType ?? 'none'}
              activeEntityType={sidebarEntityType}
              onEntitySelect={handleSidebarEntitySelect}
            />
          </div>
        </div>
      </div>
    </SimpleBar>
  );
};

export default Payments;
